import { navigationStates } from '../../enums/navigationStates.js';
import { api as navigatorApi } from '../AppTopbar/navigatorActions.js';
import { api as overviewTabApi } from './overviewTabActions';
import { api as attachmentGridApi} from '../AssetPage/attachmentTabGridActions';
import {getEnumValue, enumTypes, getEnumLabel} from '../../utils/enumHelper';
import * as lotsService from '../../services/Lots/lotsService';
import Network from 'infrastructure/js/modules/network';
import {api as messageDialogApi} from '../MessageDialog/messageDialogActions';
import * as dialogHelper from 'infrastructure/js/components/Dialog/dialogHelper';
import {isLotAccessible} from '../../utils/assetHelper';
import {createLabelHelper} from 'infrastructure/js/utils/labelHelper';
import {api as batchJobsApi} from '../BatchJobs/batchJobsActions';
import {api as activityLogTabGridApi} from '../LotPage/activityLogTabGridActions';

/////////////////////////////////////////
// ACTION TYPES - PUBLIC, FOR REDUCERS
export const actionTypes = {
  LOT_PAGE_FETCHDATA_FINISHED: 'LOT_PAGE_FETCHDATA_FINISHED',
  LOT_PAGE_CLEAR_DATA: 'LOT_PAGE_CLEAR_DATA',
  LOT_PAGE_UNMOUNT: 'LOT_PAGE_UNMOUNT',
  LOT_PAGE_SET_BUSY: 'LOT_PAGE_SET_BUSY',
};

////////////////////////////////////////////////////////////////
// PLAIN ACTION CREATORS - PRIVATE, FOR LOCAL DISPATCH ONLY
const actions = {
  fetchDataFinished: function (payload) {
    return { type: actionTypes.LOT_PAGE_FETCHDATA_FINISHED, payload: payload };
  },
  clearData: function (payload) {
    return { type: actionTypes.LOT_PAGE_CLEAR_DATA, payload: payload };
  },
  unmount: function () {
    return { type: actionTypes.LOT_PAGE_UNMOUNT };
  },
  setBusy: function (payload) {
    return {type: actionTypes.LOT_PAGE_SET_BUSY, payload: payload};
  },
};

/////////////////////////////////////////////////////
// METHODS FOR JSX PROPS - PUBLIC, ALL THUNK TYPE
export let jsxActions = {};

jsxActions.init = function (lotId) {
  return function (dispatch, getState) {
    api.fetchData(dispatch, getState)(lotId, true);
  };
};

jsxActions.unmount = function() {
  return function(dispatch, getState) {
    dispatch(actions.unmount());
  }
};

jsxActions.updateLot = function (lotId, lotUpdateData) {
  return function (dispatch, getState) {
    api.updateLot(dispatch, getState)(lotId, lotUpdateData)
  };
};

jsxActions.clearFilters = function () {
  return function (dispatch, getState) {
    activityLogTabGridApi.clearFilters(dispatch, getState)();
  };
};

/////////////////////////////////////////////////////////////////////////
// API METHODS - PUBLIC, FOR OTHER ACTION MODULES (and internal use)
export let api = {};

api.fetchData = function (dispatch, getState) {
  return function (lotId, isNewLot = false) {

    navigatorApi.setLoading(isNewLot)(dispatch, getState);

    const overviewTabPromise = overviewTabApi.init(dispatch, getState)(lotId);
    const attachmentGridPromise = attachmentGridApi.reload(dispatch, getState)();

    Promise.all([overviewTabPromise, attachmentGridPromise])
      .then((allResults) => {
        const lotData = allResults[0];
        lotData.objectType = getEnumValue(enumTypes.OBJECT_TYPE)('LOT')
        let topNavigatorData = _createNavigatorData(lotData);
        navigatorApi.setData(dispatch, getState)(topNavigatorData);

        dispatch(actions.fetchDataFinished(lotData));
        navigatorApi.setLoading(false)(dispatch, getState);
      })
      .catch((err) => {
        navigatorApi.setLoading(false)(dispatch, getState);
        console.error('Fetch shift types data failed.', err);
      });
  };
};

api.updateLot = function (dispatch, getState) {
  return function (lotId, lotUpdateData) {

    dispatch(actions.setBusy(true));

    lotUpdateData.ignoreValidationWarnings = false;  //Note: handle warning/error validation witout dialog

    lotsService.updateLotStatus(lotId, lotUpdateData).then((response) => {

      dispatch(actions.setBusy(false));

      let validations = dialogHelper.getResponseValidationDetails(response);
      if (validations) {
        let val = dialogHelper.getValidationArea(validations);

        if(response.applicationResponseStatus.statusType === 'WARNING') {
          let confirmationCallback = lotsService.updateLotStatus;
          let confirmationCallbackData = {...lotUpdateData, ignoreValidationWarnings: true};

          let confirmationConfig = {
            title: val?.[0]?.title,
            message: val?.[0]?.messages?.[0]?.message ?? '',
            cancelHandler: messageDialogApi.close(dispatch, getState),
            submitButtonLabel: 'OK',
            submitHandler: () => api.handleConfirmation(dispatch, getState)(confirmationCallback, confirmationCallbackData, lotId)
          }
          messageDialogApi.showConfirmation(dispatch, getState)(confirmationConfig)
        }
        else if (response.applicationResponseStatus.statusType === 'VALIDATION_ERROR') {

          let errorConfig = {
            title: val?.[0]?.title,
            message: val?.[0]?.messages?.[0]?.message ?? '',
            type: 'error',
          }
          messageDialogApi.showMessage(dispatch, getState)(errorConfig);

          api.fetchData(dispatch, getState)(lotId);
        }
        return {success: false};
      }

      if (!Network.isResponseValid(response)) {
        messageDialogApi.responseError(dispatch, getState)(response);
        console.error('Update Lot Failed', response);
        return {success: false};
      }

      const isReadyForInspection = (lotUpdateData?.status === getEnumValue(enumTypes.LOT_STATUS)('READY_FOR_INSPECTION'));

      if (isReadyForInspection) {
        dispatch(actions.setBusy(true));
        return api.handlePostSubmitFlow(dispatch, getState)(lotId);
      }

      return api.fetchData(dispatch, getState)(lotId);
    })
    .catch((error) => {
      console.error('updateLotStatus(): failed, catch error: ', error);
      dispatch(actions.setBusy(false));
      api.fetchData(dispatch, getState)(lotId);
    });
  }
}

api.handleConfirmation = function(dispatch, getState) {
  return function (confirmationCallback, confirmationCallbackData, lotId) {
    messageDialogApi.close(dispatch, getState)();

    dispatch(actions.setBusy(true));

    confirmationCallbackData.ignoreValidationWarnings = true;

    confirmationCallback?.(lotId, confirmationCallbackData).then((response) => {

      dispatch(actions.setBusy(false));

      if (!Network.isResponseValid(response)) {
        messageDialogApi.responseError(dispatch, getState)(response);
        console.error('Update Lot Failed', response);
      }

      // Update Lot page to reload everything.
      api.fetchData(dispatch, getState)(lotId);
    })
    .catch((error) => {
      console.error('handleConfirmation(): failed, catch error: ', error);
      dispatch(actions.setBusy(false));
      api.fetchData(dispatch, getState)(lotId);
    });
  }
}

api.handlePostSubmitFlow = function(dispatch, getState) {
  return function (lotId) {
    return api.checkCocCsvAutomation(dispatch, getState)(lotId)
      .then((res) => {
        dispatch(actions.setBusy(false));
        api.fetchData(dispatch, getState)(lotId);

        if (res?.data?.automationAvailable) {
          return api.submitWithConfirmation(dispatch, getState)(lotId);
        }
      });
  }
}

api.checkCocCsvAutomation = function(dispatch, getState) {
  return function (lotId) {
    return lotsService.checkCocCsvAutomation(lotId)
      .then((response) => {
        if (!Network.isResponseValid(response)) {
          console.error('checkCocCsvAutomation() failed ', response);
          messageDialogApi.responseError(dispatch, getState)(response);
          return null;
        }
        return response;
      })
      .catch((error) => {
        console.error('checkCocCsvAutomation() failed, catch error: ', error);
        return null;
      });
  };
}

api.submitWithConfirmation = function (dispatch, getState) {
  return function (lotId) {
    let labels = createLabelHelper('mat.dialog.editStatus.');

    let confirmationConfig = {
      message: labels.get('confirmationBefore.message.title'),
      submitButtonLabel: labels.get('confirmationBefore.footer.ok'),
      cancelHandler: messageDialogApi.close(dispatch, getState),
      submitHandler: () => api.doSubmit(dispatch, getState)(lotId),
    };

    messageDialogApi.showConfirmation(dispatch, getState)(confirmationConfig);
  }
}

api.doSubmit = function (dispatch, getState) {
  return function (lotId) {
    messageDialogApi.close(dispatch, getState)();
    api.submitWithProgress(dispatch, getState)(lotId);
  }
}

api.submitWithProgress = function (dispatch, getState) {
  return function (lotId) {

    lotsService.startCocCsvAutomation(lotId)
      .then((response) => {
        api.fetchData(dispatch, getState)(lotId);

        if (!Network.isResponseValid(response)) {
          console.error('startCocCsvAutomation(): failed', response);
          messageDialogApi.responseError(dispatch, getState)(response);
          return { success: false };
        }

        batchJobsApi
          .runJob(dispatch, getState)(response.data)
          .then((jobResponse) => {
            api.fetchData(dispatch, getState)(lotId);
            return { success: true };
          })
          .catch((error) => {
            console.error('runJob failed, catch error: ', error);
            return { success: false };
          });
      })
      .catch((error) => {
        console.error('startCocCsvAutomation():) failed, catch error: ', error);
        api.fetchData(dispatch, getState)(lotId);
      });
  };
}

/////////////////////////////////////////////////////////////////////////
// PRIVATE HELPERS
function _createNavigatorData(lotData) {
  return {
    id: lotData.id,
    name: lotData.name,
    type: navigationStates.LOT,
    // status: _getStatus(lotData),
  }
}

function _getStatus(lot) {
  return !isLotAccessible(lot?.status) ? getEnumLabel(enumTypes.LOT_STATUS)(lot?.status) : '';
}
